import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import api from "@/lib/api";
import { getPublicOrigin, getPublicUrl } from "@/lib/urls";
import { useSeo, setJsonLd, breadcrumbSchema } from "@/lib/seo";
import { Check, Globe, ArrowRight } from "lucide-react";

const SENT_COLOR = {
  positive: "bg-emerald-100 text-emerald-800 border-emerald-300",
  neutral: "bg-zinc-100 text-zinc-800 border-zinc-300",
  negative: "bg-rose-100 text-rose-800 border-rose-300",
};

const ENGINE_LABEL = {
  openai: "ChatGPT",
  anthropic: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
  google_aio: "AI Overview",
};

export default function AtlasView() {
  const { country, brand: slug } = useParams();
  const [atlas, setAtlas] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  // SEO — title/description/canonical/og/twitter all derived from the
  // current window origin via getPublicUrl(). Never an env var.
  useSeo({
    title: atlas
      ? `${atlas.brand} AI visibility in ${atlas.country_label} · AnswerWatch Atlas`
      : "AnswerWatch Atlas",
    description: atlas
      ? `Live report: is ${atlas.brand} recommended by AI engines (ChatGPT, Claude, Gemini) in ${atlas.country_label}? Mention rate, citation rate, and prompt-level detail.`
      : "Live AI visibility report — ChatGPT, Claude & Gemini side by side.",
    path: `/atlas/${country}/${slug}`,
    type: "article",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/atlas/${country}/${slug}`);
        setAtlas(r.data);
        const canonicalUrl = getPublicUrl(`/atlas/${country}/${slug}`);
        const orgUrl = getPublicOrigin() + "/";
        // Report schema — every url/@id derived via the helper.
        setJsonLd("atlas-jsonld", {
          "@context": "https://schema.org",
          "@type": "Report",
          "@id": canonicalUrl + "#report",
          name: `${r.data.brand} AI visibility in ${r.data.country_label}`,
          url: canonicalUrl,
          mainEntityOfPage: canonicalUrl,
          dateCreated: r.data.created_at,
          author: {
            "@type": "Organization",
            "@id": getPublicOrigin() + "/#organization",
            name: "AnswerWatch",
            url: orgUrl,
          },
          publisher: { "@id": getPublicOrigin() + "/#organization" },
          about: { "@type": "Thing", name: r.data.brand },
          abstract: `AI visibility report for ${r.data.brand} in ${r.data.country_label}.`,
        });
        // Breadcrumb schema for /atlas → /atlas/:country → this report
        setJsonLd(
          "atlas-breadcrumb-jsonld",
          breadcrumbSchema([
            { name: "Atlas", path: "/atlas" },
            {
              name: r.data.country_label,
              path: `/atlas?country=${country}`,
            },
            { name: r.data.brand, path: `/atlas/${country}/${slug}` },
          ])
        );
      } catch {
        setError(true);
      }
    })();
  }, [country, slug]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!email.trim() || !atlas) return;
    try {
      await api.post(`/public/atlas/${atlas.atlas_id}/lead`, { email: email.trim() });
      setLeadSubmitted(true);
    } catch {
      /* silent */
    }
  };

  if (error) {
    return (
      <div className="bg-white min-h-screen">
        <MarketingNav />
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <h1 className="display text-3xl font-black">Atlas not found</h1>
          <p className="mt-4 text-zinc-600 text-sm">
            This atlas may have been removed or never existed.{" "}
            <Link to="/atlas" className="underline">
              Run your own free atlas
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  if (!atlas) {
    return (
      <div className="bg-white min-h-screen">
        <MarketingNav />
        <div className="max-w-xl mx-auto px-6 py-24 text-center text-zinc-500 text-sm">
          Loading atlas…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="atlas-view">
      <MarketingNav />
      <article className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="pill" data-testid="atlas-country">
          <Globe size={11} /> Atlas · {atlas.country_label}
        </div>
        <h1 className="display mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[0.98]">
          {atlas.brand} AI visibility in {atlas.country_label}
        </h1>
        <p className="mt-4 text-lg text-zinc-600 max-w-2xl">
          Live scan across ChatGPT, Claude and Gemini. {atlas.metrics?.mentioned_count ?? 0} of{" "}
          {atlas.metrics?.total_results ?? 0} AI responses mentioned {atlas.brand}.
        </p>

        {/* Headline stats */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Mention rate"
            value={`${atlas.metrics?.mention_rate ?? 0}%`}
            helper="Answers that name the brand"
          />
          <Stat
            label="Citation rate"
            value={`${atlas.metrics?.citation_rate ?? 0}%`}
            helper="Mentions with a URL nearby"
          />
          <Stat
            label="BVS score"
            value={atlas.metrics?.bvs_score ?? 0}
            helper="Out of 100"
          />
          <Stat
            label="Engines"
            value={(atlas.matrix?.engines || []).length}
            helper="Run in parallel"
          />
        </div>

        {/* Engine breakdown */}
        {atlas.metrics?.engine_breakdown && (
          <section className="mt-10 card-flat p-5">
            <div className="label-eyebrow">Engine breakdown</div>
            <div className="mt-4 space-y-3">
              {Object.entries(atlas.metrics.engine_breakdown).map(([eng, b]) => (
                <div
                  key={eng}
                  className="flex items-center gap-3"
                  data-testid={`engine-${eng}`}
                >
                  <div className="w-28 text-sm font-medium">
                    {ENGINE_LABEL[eng] || eng}
                  </div>
                  <div className="flex-1 bg-zinc-100 h-2 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-zinc-900"
                      style={{ width: `${b.rate}%` }}
                    />
                  </div>
                  <div className="w-16 text-right mono text-xs">
                    {b.mentioned}/{b.total}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Matrix */}
        <Matrix atlas={atlas} />

        {/* Lead capture */}
        <section className="mt-12 card-flat p-6" data-testid="atlas-lead">
          <div className="grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7">
              <div className="label-eyebrow">Full report · email me a copy</div>
              <h3 className="display mt-1 text-2xl font-bold tracking-tight">
                Want the PDF, competitor breakdown, and weekly tracking?
              </h3>
              <p className="mt-2 text-sm text-zinc-600">
                Drop your email. We'll send you the full PDF and a demo of how
                to turn this atlas into your own weekly monitor.
              </p>
            </div>
            <div className="md:col-span-5">
              {!leadSubmitted ? (
                <form onSubmit={submitLead} className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="atlas-lead-email"
                    className="flex-1 bg-white border border-zinc-200 rounded-[4px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                  <button
                    data-testid="atlas-lead-submit"
                    className="bg-black text-white text-sm px-4 py-2.5 rounded-[4px] hover:bg-zinc-800 transition-colors"
                  >
                    Send it
                  </button>
                </form>
              ) : (
                <div
                  className="flex items-center gap-2 text-sm text-emerald-700"
                  data-testid="atlas-lead-success"
                >
                  <Check size={14} /> On its way — check your inbox.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA: Run your own / start tracking */}
        <section className="mt-10 text-center py-10 border-t border-zinc-200">
          <div className="label-eyebrow">Powered by AnswerWatch</div>
          <h3 className="display mt-2 text-3xl font-black tracking-tighter">
            Turn this atlas into a weekly monitor.
          </h3>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link
              to={`/atlas?brand=${encodeURIComponent(atlas.brand)}&domain=${encodeURIComponent(atlas.domain || "")}`}
              data-testid="atlas-run-another"
              className="border border-zinc-900 text-sm px-4 py-2.5 rounded-[4px] hover:bg-zinc-100"
            >
              Run another locale
            </Link>
            <Link
              to="/login"
              data-testid="atlas-start-trial"
              className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-4 py-2.5 rounded-[4px] hover:bg-zinc-800"
            >
              Start free trial <ArrowRight size={14} />
            </Link>
          </div>
        </section>

        <footer className="mt-12 border-t border-zinc-200 pt-6 flex items-center justify-between text-xs text-zinc-500">
          <span className="mono uppercase tracking-[0.2em]">
            Atlas {atlas.atlas_id.slice(0, 12)} · {atlas.views} view
            {atlas.views === 1 ? "" : "s"}
          </span>
          <span className="mono uppercase tracking-[0.2em]">
            Generated{" "}
            {atlas.completed_at
              ? new Date(atlas.completed_at).toLocaleDateString()
              : ""}
          </span>
        </footer>
      </article>
    </div>
  );
}

function Matrix({ atlas }) {
  const [selected, setSelected] = useState(null);
  const prompts = atlas.matrix?.prompts || [];
  const engines = atlas.matrix?.engines || [];
  const languages = atlas.matrix?.languages || [];
  const cells = atlas.matrix?.cells || {};

  const rows = useMemo(() => prompts.map((p) => ({ ...p })), [prompts]);

  if (!prompts.length) return null;

  return (
    <section className="mt-10 card-flat overflow-hidden" data-testid="atlas-matrix">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Prompt-level results</div>
          <div className="mt-1 text-sm text-zinc-500">
            Click any cell to read the full AI response.
          </div>
        </div>
        <span className="pill mono text-[10px]">
          {prompts.length} prompts · {engines.length} engines
        </span>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#fafafa] border-b border-zinc-200">
            <tr>
              <th className="sticky left-0 bg-[#fafafa] text-left p-3 label-eyebrow w-[360px] min-w-[360px] z-10 border-r border-zinc-200">
                Prompt
              </th>
              {engines.map((eng) => (
                <th
                  key={eng}
                  className="text-center p-2 label-eyebrow"
                >
                  {ENGINE_LABEL[eng] || eng}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.prompt_id} className={i % 2 ? "bg-[#fafafa]/30" : "bg-white"}>
                <td
                  className={`sticky left-0 p-3 border-r border-zinc-200 max-w-[360px] z-10 ${
                    i % 2 ? "bg-[#fcfcfc]" : "bg-white"
                  }`}
                >
                  <div className="text-xs font-medium">{p.text}</div>
                </td>
                {engines.map((eng) => {
                  const lang = languages[0];
                  const cell = cells?.[p.prompt_id]?.[lang]?.[eng];
                  return (
                    <td
                      key={eng}
                      className="text-center p-2"
                    >
                      <Cell
                        cell={cell}
                        onOpen={() => setSelected({ cell, prompt: p, engine: eng })}
                        testid={`atlas-cell-${p.prompt_id}-${eng}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected?.cell && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="atlas-cell-modal"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white border border-zinc-200 rounded-[6px] max-w-3xl w-full max-h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-zinc-200">
              <div className="label-eyebrow">
                {ENGINE_LABEL[selected.engine] || selected.engine}
              </div>
              <h3 className="display mt-1 text-lg font-bold tracking-tight">
                {selected.prompt.text}
              </h3>
            </div>
            <div className="p-5 overflow-auto">
              <pre className="whitespace-pre-wrap text-sm text-zinc-800 bg-[#fafafa] border border-zinc-200 rounded-[4px] p-3 max-h-[60vh] overflow-auto">
                {selected.cell.response_excerpt || "(no excerpt)"}
              </pre>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Cell({ cell, onOpen, testid }) {
  if (!cell) {
    return (
      <span className="inline-block w-8 h-8 rounded-[3px] border border-zinc-200 bg-white text-zinc-300 text-xs leading-8" data-testid={testid}>
        ·
      </span>
    );
  }
  if (cell.error) {
    return (
      <span className="inline-block w-8 h-8 rounded-[3px] border border-rose-300 bg-rose-50 text-rose-700 text-xs leading-8" data-testid={testid}>
        !
      </span>
    );
  }
  if (!cell.mentioned_brand) {
    return (
      <button
        onClick={onOpen}
        data-testid={testid}
        className="inline-block w-8 h-8 rounded-[3px] border border-zinc-200 bg-white text-zinc-400 text-xs leading-8 hover:bg-zinc-100"
      >
        ✗
      </button>
    );
  }
  const cls = SENT_COLOR[cell.sentiment] || SENT_COLOR.neutral;
  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      className={`inline-block w-8 h-8 rounded-[3px] border ${cls} text-xs leading-8 font-semibold hover:brightness-95`}
    >
      ✓
    </button>
  );
}

function Stat({ label, value, helper }) {
  return (
    <div className="card-flat p-4">
      <div className="label-eyebrow">{label}</div>
      <div className="display text-2xl font-black mt-2 tabular-nums">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{helper}</div>
    </div>
  );
}
